import { useCallback, useMemo } from "react";

import {
    useCustomerIdentityAttrs,
    useFireAnalyticsEvents
} from "@/components/fastshift/analytics";
import {
    SourceKey,
    ActionKey,
    ActionSubjectKey,
    ActionSubjectIdKey,
    SubjectAttributeKey
} from "@/components/fastshift/analytics/constants";
import { isSupportRequestOpen } from "@/components/fastshift/src/common/relationships/src/utils";
import { useFastshiftSelector } from "@/components/fastshift/src/state";

export const useDashboardV2UIAnalytics = () => {
    const { data: supportRequests } = useFastshiftSelector(
        (state) => state.supportRequests
    );
    const identityAttrs = useCustomerIdentityAttrs(true);
    const { fireUIEvent } = useFireAnalyticsEvents(identityAttrs);

    const hasRequestAssistancePathway = useMemo(
        () =>
            !!supportRequests?.some((req) => isSupportRequestOpen(req.status)),
        [supportRequests]
    );

    // UI event callbacks
    const sendOrgSelectorDropdownClickUIEvent = useCallback(() => {
        fireUIEvent({
            action: ActionKey.CLICKED,
            actionSubject: ActionSubjectKey.DROPDOWN,
            actionSubjectId: ActionSubjectIdKey.ORG_SELECTOR_DROPDOWN,
            source: SourceKey.FASTSHIFT_PORTAL_FOR_YOU_SCREEN,
            attributes: {
                hasRequestAssistancePathway
            }
        });
    }, [fireUIEvent, hasRequestAssistancePathway]);

    const sendOrgSelectorDropdownItemClickUIEvent = useCallback(
        (selectedOrgId: string) => {
            fireUIEvent({
                action: ActionKey.CLICKED,
                actionSubject: ActionSubjectKey.DROPDOWN_ITEM,
                actionSubjectId: ActionSubjectIdKey.ORG_SELECTOR_DROPDOWN,
                source: SourceKey.FASTSHIFT_PORTAL_FOR_YOU_SCREEN,
                attributes: {
                    selectedOrgId
                }
            });
        },
        [fireUIEvent]
    );

    const sendMigrationSummarySectionLinkClickUIEvent = useCallback(
        (subject: SubjectAttributeKey, selectedOrgId: string) => {
            fireUIEvent({
                action: ActionKey.CLICKED,
                actionSubject: ActionSubjectKey.LINK,
                actionSubjectId: ActionSubjectIdKey.MIGRATION_SUMMARY_SECTION,
                source: SourceKey.FASTSHIFT_PORTAL_FOR_YOU_SCREEN,
                attributes: {
                    subject,
                    selectedOrgId
                }
            });
        },
        [fireUIEvent]
    );

    const sendMigrationProgressTableOpenSupportTicketClickUIEvent = useCallback(
        (senId: string, ticketId: string) => {
            fireUIEvent({
                action: ActionKey.CLICKED,
                actionSubject: ActionSubjectKey.LINK,
                actionSubjectId:
                    ActionSubjectIdKey.FASTSHIFT_MIGRATION_PROGRESS_TABLE_OPEN_SUPPORT_TICKET_CLICK,
                source: SourceKey.FASTSHIFT_PORTAL_FOR_YOU_SCREEN,
                attributes: {
                    senId,
                    ticketId
                }
            });
        },
        [fireUIEvent]
    );

    return {
        sendOrgSelectorDropdownClickUIEvent,
        sendOrgSelectorDropdownItemClickUIEvent,
        sendMigrationSummarySectionLinkClickUIEvent,
        sendMigrationProgressTableOpenSupportTicketClickUIEvent
    };
};
